import { ReactNode, useState } from "react"
import cx from "classnames"
import { Autocomplete, TextField, Select, SelectChangeEvent, Fade } from "@mui/material"

import { AutocompleteProps, SelectorProps } from "@/types/components/selector"
import Icon from "@/components/icon/Icon"
import { colorTokens } from "@/components/colorTokens"

import "./selector.scss"

function AutoCompleteSelector<T>({
  renderOption,
  onChange,
  value,
  className,
  showMessage,
  message,
  labelKey = "label",
  renderInput,
  label,
  error,
  placeholder,
  prependIcon,
  readOnly = true,
  ...props
}: AutocompleteProps<T>) {
  const [open, setOpen] = useState(false)

  return (
    <div className={cx("SelectorCont", className, { fullWidth: props.fullWidth, error })}>
      {label && <p className="selector-label">{label}</p>}
      <Autocomplete
        {...props}
        open={open}
        onOpen={(event) => {
          setOpen(true)
          props.onOpen?.(event)
        }}
        onClose={(event, reason) => {
          setOpen(false)
          props.onClose?.(event, reason)
        }}
        getOptionLabel={(option: any) => {
          if (typeof option !== "object") {
            return option
          }

          return option[labelKey]
        }}
        getOptionKey={props.getOptionKey}
        renderInput={
          renderInput
            ? (params) => renderInput({ ...params, isOpen: open, setOpen })
            : (params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  error={error}
                  onMouseDown={(e) => {
                    if (open) {
                      e.preventDefault()
                      setOpen(false)
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: prependIcon && (
                      <span style={{ display: "flex", paddingRight: "6px" }}>{prependIcon}</span>
                    ),
                    endAdornment: (
                      <span
                        onMouseDown={(e) => {
                          if (props.disabled) {
                            return
                          }
                          e.preventDefault()
                          e.stopPropagation()
                          setOpen(!open)
                        }}
                        style={{ cursor: props.disabled ? "default" : "pointer", display: "flex" }}>
                        <Icon
                          type={open ? "arrow-up" : "arrow-down"}
                          color={colorTokens.field["ico-base"]}
                          size={16}
                        />
                      </span>
                    ),
                  }}
                  inputProps={{
                    ...params.inputProps,
                    readOnly,
                  }}
                />
              )
        }
        renderOption={renderOption as (value: unknown) => ReactNode}
        onChange={onChange as (event: unknown) => void}
        value={value as T}
        clearIcon={"clearIcon" in props ? props.clearIcon : null}
        slotProps={{
          paper: { className: "AutocompleteCont-paper" },
          popper: {
            ...(props.slotProps?.popper as object),
            className: cx(
              "AutocompleteCont-popper",
              (props.slotProps?.popper as { className?: string })?.className,
            ),
          },
        }}
      />
      {showMessage && <p className="msg">{message}</p>}
    </div>
  )
}

function BasicSelector<T>({
  renderValue,
  onChange,
  value,
  className,
  showMessage,
  message,
  IconComponent,
  open,
  onOpen,
  onClose,
  ...props
}: SelectorProps<T>) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = open !== undefined ? open : internalOpen

  const handleOpen = (event?: React.SyntheticEvent) => {
    if (open === undefined) {
      setInternalOpen(true)
    }
    if (event) {
      onOpen?.(event)
    }
  }

  const handleClose = (event?: React.SyntheticEvent) => {
    if (open === undefined) {
      setInternalOpen(false)
    }
    if (event) {
      onClose?.(event)
    }
  }

  const handleIconClick = (e: React.MouseEvent) => {
    if (props.disabled) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    if (isOpen) {
      handleClose(e)
    } else {
      handleOpen(e)
    }
  }

  return (
    <div className={cx("SelectorCont", className)}>
      <Select
        {...props}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        MenuProps={{
          ...props.MenuProps,
          className: "SelectorCont-menu",
          disableScrollLock: true,
          TransitionComponent: Fade,
        }}
        renderValue={renderValue as (value: unknown) => string}
        onChange={onChange as (event: SelectChangeEvent<unknown>, child?: unknown) => void}
        value={value as unknown}
        IconComponent={
          IconComponent ||
          (() => (
            <span
              onMouseDown={handleIconClick}
              style={{ cursor: "pointer", display: "flex", pointerEvents: "auto" }}>
              <Icon
                type={isOpen ? "arrow-up" : "arrow-down"}
                color={colorTokens.field["ico-base"]}
                size={16}
              />
            </span>
          ))
        }
      />
      {showMessage && <p className="msg">{message}</p>}
    </div>
  )
}

export default function Selector<T>({
  type = "autocomplete",
  ...props
}: AutocompleteProps<T> | SelectorProps<T>) {
  if (type === "autocomplete") {
    return <AutoCompleteSelector {...(props as AutocompleteProps<T>)} />
  }

  return <BasicSelector {...(props as SelectorProps<T>)} />
}
